import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { HTML_LANG, OG_LOCALE, localeFromPath, stripLocalePrefix } from "@/lib/locale";

const SITE_ORIGIN = "https://frac.io";

/**
 * Per-page SEO tags. Every marketing page renders one <PageSeo />.
 *
 * Bilingual behaviour:
 *   - <html lang> is managed globally by <LocaleProvider>.
 *   - We emit canonical + hreflang alternate links (en-SG, zh-Hans, x-default)
 *     so search engines can pair the two language variants.
 *   - og:locale reflects the current locale.
 *
 * Legal pages (/privacy, /terms) opt out of hreflang by passing
 * `disableAlternates`, since they only exist in English.
 *
 * Props:
 *   title       — full <title> string, verbatim.
 *   description — meta description
 *   path        — canonical (English) path, e.g. "/platform/marketing"
 *   image       — OG image path
 *   type        — og:type
 *   children    — extra <meta> or <script>
 *   disableAlternates — set for legal / one-language-only pages
 */
export function PageSeo({
  title,
  description,
  path = "/",
  image = "/og-default.png",
  type = "website",
  children,
  disableAlternates = false,
}) {
  const { pathname } = useLocation();
  const currentLocale = localeFromPath(pathname);
  const canonicalPath = stripLocalePrefix(path);
  const canonicalUrl =
    currentLocale === "zh"
      ? `${SITE_ORIGIN}/zh${canonicalPath === "/" ? "" : canonicalPath}`
      : `${SITE_ORIGIN}${canonicalPath}`;

  const enUrl = `${SITE_ORIGIN}${canonicalPath}`;
  const zhUrl = `${SITE_ORIGIN}/zh${canonicalPath === "/" ? "" : canonicalPath}`;

  const ogImage = image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {!disableAlternates && (
        <link rel="alternate" hrefLang="en-SG" href={enUrl} />
      )}
      {!disableAlternates && (
        <link rel="alternate" hrefLang="zh-Hans" href={zhUrl} />
      )}
      {!disableAlternates && (
        <link rel="alternate" hrefLang="x-default" href={enUrl} />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Frac" />
      <meta property="og:locale" content={OG_LOCALE[currentLocale]} />
      {!disableAlternates && currentLocale === "en" && (
        <meta property="og:locale:alternate" content={OG_LOCALE.zh} />
      )}
      {!disableAlternates && currentLocale === "zh" && (
        <meta property="og:locale:alternate" content={OG_LOCALE.en} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <html lang={HTML_LANG[currentLocale]} />

      {children}
    </Helmet>
  );
}

export default PageSeo;
